import { userRequest } from "./requestMethods";

/**
 * Order together (group baskets): the client half of
 * Lusuku_Dashboard_Node-Server/docs/orders/group-baskets-contract.md.
 *
 * Every call goes through userRequest, which sends x-country and, when the
 * shopper is signed in, the bearer token. The server shapes every basket for
 * the caller and is the single source of truth: these functions return its
 * answers untouched and never add anything up.
 *
 * A refusal is thrown as a GroupBasketError carrying the server's `code`,
 * customer-ready `message`, HTTP status and any extra fields (state,
 * basketId, minimumOrder, pendingMemberIds, nextOpening, ...). Screens branch
 * on `code`, never on `message`.
 */

const BASE = "/api/group-baskets";

const NETWORK_MESSAGE = "We could not reach Lusuku. Check your connection and try again.";

export class GroupBasketError extends Error {
  constructor({ status = 0, code = "NETWORK_ERROR", message = NETWORK_MESSAGE, ...extra } = {}) {
    super(message || NETWORK_MESSAGE);
    this.name = "GroupBasketError";
    this.status = status;
    this.code = code || "SERVER_ERROR";
    this.extra = extra;
  }
}

/* Any axios failure as a GroupBasketError (exported for the tests). */
export const groupBasketErrorOf = (error) => {
  if (error instanceof GroupBasketError) return error;
  const response = error?.response;
  if (!response) return new GroupBasketError();
  const body = response.data && typeof response.data === "object" ? response.data : {};
  const { success, code, message, ...extra } = body;
  return new GroupBasketError({
    status: response.status,
    code: code || (response.status === 401 ? "NOT_AUTHENTICATED" : "SERVER_ERROR"),
    message: message || "Something went wrong. Please try again.",
    ...extra,
  });
};

const call = async (request) => {
  try {
    const response = await request();
    return response.data;
  } catch (error) {
    throw groupBasketErrorOf(error);
  }
};

const basketOf = (data) => data?.basket || null;

/* 5.1 Whether to show "Order together" at all, for this market. */
export const fetchGroupOrderingAvailability = () =>
  call(() => userRequest.get(`${BASE}/availability`));

/* 5.2 Open a basket as its host. */
export const createGroupBasket = (body) =>
  call(() => userRequest.post(BASE, body)).then(basketOf);

/*
 * 5.3 The baskets this shopper is in (active, and ended in the last 7 days),
 * with the server's clock for their countdowns (`serverNow`; null until the
 * server sends it, and the countdown then reads this device's clock).
 */
export const fetchMyGroupBaskets = () =>
  call(() => userRequest.get(`${BASE}/mine`)).then((data) => ({
    baskets: Array.isArray(data?.baskets) ? data.baskets : [],
    serverNow: data?.serverNow || null,
  }));

/* 5.4 One basket, shaped for the caller. */
export const fetchGroupBasket = (basketId) =>
  call(() => userRequest.get(`${BASE}/${encodeURIComponent(basketId)}`)).then(basketOf);

/* 5.5 The link preview (safe signed out) and the join itself. */
export const fetchGroupJoinPreview = (token) =>
  call(() => userRequest.get(`${BASE}/join/${encodeURIComponent(token)}`)).then(
    (data) => data?.preview || null
  );

export const joinGroupBasket = (token, label) =>
  call(() =>
    userRequest.post(`${BASE}/join/${encodeURIComponent(token)}`, label ? { label } : {})
  ).then(basketOf);

/* 5.6 Replace the caller's own lines: ids, quantities and choices only. */
export const saveMyGroupLines = (basketId, lines) =>
  call(() => userRequest.put(`${BASE}/${encodeURIComponent(basketId)}/lines`, { lines })).then(
    basketOf
  );

/* 5.7 Leave, remove a member (OPEN) or drop an unpaid member (AWAITING_HOST). */
export const removeGroupMember = (basketId, memberId) =>
  call(() =>
    userRequest.delete(
      `${BASE}/${encodeURIComponent(basketId)}/members/${encodeURIComponent(memberId)}`
    )
  ).then(basketOf);

/* 5.8 The host locks now, before the cut-off. */
export const lockGroupBasket = (basketId) =>
  call(() => userRequest.post(`${BASE}/${encodeURIComponent(basketId)}/lock`, {})).then(basketOf);

/* 5.9 HOST_PAYS_ALL: place the one order. Returns { order, basket }. */
export const checkoutGroupBasket = (basketId, paymentMethod) =>
  call(() =>
    userRequest.post(`${BASE}/${encodeURIComponent(basketId)}/checkout`, { paymentMethod })
  ).then((data) => ({ order: data?.order || null, basket: basketOf(data) }));

/* 5.10 EACH_PAYS_SHARE: the caller's contribution, the order their share is paid on. */
export const fetchMyShareContribution = (basketId) =>
  call(() => userRequest.post(`${BASE}/${encodeURIComponent(basketId)}/shares/mine`, {})).then(
    (data) => ({ version: data?.version ?? null, contribution: data?.contribution || null })
  );

/* 5.11 EACH_PAYS_SHARE: the host's remainder contribution. */
export const fetchRemainderContribution = (basketId) =>
  call(() => userRequest.post(`${BASE}/${encodeURIComponent(basketId)}/remainder`, {})).then(
    (data) => ({ version: data?.version ?? null, contribution: data?.contribution || null })
  );

/*
 * 5.12 The host cancels. `confirmRefunds` is the explicit confirmation the
 * server asks for once a share has been paid (those shares are refunded).
 */
export const cancelGroupBasket = (basketId, { reason, confirmRefunds = false } = {}) =>
  call(() =>
    userRequest.post(`${BASE}/${encodeURIComponent(basketId)}/cancel`, {
      ...(reason ? { reason } : {}),
      ...(confirmRefunds ? { confirmRefunds: true } : {}),
    })
  ).then(basketOf);
